/**
 * QuestionCard Component
 *
 * Displays a multiple-choice question for the Story Detective quiz.
 * Handles selection, feedback, and results display.
 */

import { motion } from 'framer-motion'
import { Check, X, HelpCircle } from 'lucide-react'
import { Card, CardContent } from '@/components/ui'
import { cn } from '@/lib/utils'
import { getElementDisplayName } from '@/services/detectiveService'
import type { StoryElement } from '@/types/domain'

interface DetectiveQuestion {
  element: StoryElement
  question: string
  options: string[]
  correct_index: number
}

// =============================================================================
// Question Card (During Quiz)
// =============================================================================

interface QuestionCardProps {
  question: DetectiveQuestion
  questionNumber: number
  totalQuestions: number
  selectedAnswer: number | null
  onSelect: (index: number) => void
  disabled?: boolean
}

export function QuestionCard({
  question,
  questionNumber,
  totalQuestions,
  selectedAnswer,
  onSelect,
  disabled = false,
}: QuestionCardProps) {
  const elementName = getElementDisplayName(question.element)

  return (
    <Card>
      <CardContent className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <span className="text-sm font-medium text-[var(--color-kids-primary)]">
            Question {questionNumber} of {totalQuestions}
          </span>
          <span className="text-xs bg-[var(--color-kids-surface-alt)] text-[var(--color-kids-text-muted)] px-2 py-1 rounded-full">
            {elementName}
          </span>
        </div>

        {/* Question */}
        <h3 className="font-display font-bold text-lg text-[var(--color-kids-text)] mb-4">
          {question.question}
        </h3>

        {/* Options */}
        <div className="space-y-3">
          {question.options.map((option, index) => (
            <motion.button
              key={index}
              onClick={() => !disabled && onSelect(index)}
              disabled={disabled}
              className={cn(
                'w-full text-left p-4 rounded-xl border-2 transition-all',
                selectedAnswer === index
                  ? 'border-[var(--color-kids-primary)] bg-[var(--color-kids-primary)]/5'
                  : 'border-[var(--color-kids-surface-alt)] hover:border-[var(--color-kids-primary)]/50',
                disabled && 'cursor-not-allowed opacity-60'
              )}
              whileHover={!disabled ? { scale: 1.01 } : {}}
              whileTap={!disabled ? { scale: 0.99 } : {}}
            >
              <div className="flex items-center gap-3">
                <div
                  className={cn(
                    'w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0',
                    selectedAnswer === index
                      ? 'bg-[var(--color-kids-primary)] text-white'
                      : 'bg-[var(--color-kids-surface-alt)] text-[var(--color-kids-text-muted)]'
                  )}
                >
                  {String.fromCharCode(65 + index)}
                </div>
                <span className="text-[var(--color-kids-text)]">{option}</span>
              </div>
            </motion.button>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}

// =============================================================================
// Question Result Card (After Quiz)
// =============================================================================

interface QuestionResultCardProps {
  question: DetectiveQuestion
  questionNumber: number
  userAnswer: number | null
  explanation?: string
}

export function QuestionResultCard({
  question,
  questionNumber,
  userAnswer,
  explanation,
}: QuestionResultCardProps) {
  const isCorrect = userAnswer === question.correct_index
  const elementName = getElementDisplayName(question.element)

  return (
    <Card
      className={cn(
        'overflow-hidden border-l-4',
        isCorrect
          ? 'border-l-[var(--color-kids-success)]'
          : 'border-l-[var(--color-kids-danger)]'
      )}
    >
      <CardContent className="p-4">
        {/* Header */}
        <div className="flex items-center gap-2 mb-3">
          <div
            className={cn(
              'w-6 h-6 rounded-full flex items-center justify-center',
              isCorrect
                ? 'bg-[var(--color-kids-success)]/10 text-[var(--color-kids-success)]'
                : 'bg-[var(--color-kids-danger)]/10 text-[var(--color-kids-danger)]'
            )}
          >
            {isCorrect ? (
              <Check className="w-4 h-4" />
            ) : (
              <X className="w-4 h-4" />
            )}
          </div>
          <span className="text-sm font-medium text-[var(--color-kids-text)]">
            Question {questionNumber}
          </span>
          <span className="text-xs bg-[var(--color-kids-surface-alt)] text-[var(--color-kids-text-muted)] px-2 py-0.5 rounded-full">
            {elementName}
          </span>
        </div>

        {/* Question */}
        <p className="font-medium text-[var(--color-kids-text)] mb-3">
          {question.question}
        </p>

        {/* Answer display */}
        <div className="space-y-2">
          {/* Correct answer */}
          <div className="flex items-start gap-2 p-2 rounded-lg bg-[var(--color-kids-success)]/10">
            <Check className="w-4 h-4 text-[var(--color-kids-success)] mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-xs text-[var(--color-kids-success)] font-medium">
                Correct answer:
              </p>
              <p className="text-sm text-[var(--color-kids-text)]">
                {question.options[question.correct_index]}
              </p>
            </div>
          </div>

          {/* User's wrong answer */}
          {!isCorrect && userAnswer !== null && (
            <div className="flex items-start gap-2 p-2 rounded-lg bg-[var(--color-kids-danger)]/10">
              <X className="w-4 h-4 text-[var(--color-kids-danger)] mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs text-[var(--color-kids-danger)] font-medium">
                  Your answer:
                </p>
                <p className="text-sm text-[var(--color-kids-text)]">
                  {question.options[userAnswer]}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Explanation */}
        {explanation && (
          <div className="mt-3 pt-3 border-t border-[var(--color-kids-surface-alt)]">
            <div className="flex items-start gap-2">
              <HelpCircle className="w-4 h-4 text-[var(--color-kids-primary)] mt-0.5 flex-shrink-0" />
              <p className="text-sm text-[var(--color-kids-text-muted)]">
                {explanation}
              </p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

// =============================================================================
// Score Display
// =============================================================================

interface ScoreDisplayProps {
  score: number
  total: number
  label: string
  emoji: string
  color: string
}

export function ScoreDisplay({ score, total, label, emoji, color }: ScoreDisplayProps) {
  const percentage = Math.round((score / total) * 100)

  return (
    <div className="text-center">
      {/* Big emoji */}
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: 'spring', stiffness: 200, delay: 0.2 }}
        className="text-6xl mb-4"
      >
        {emoji}
      </motion.div>

      {/* Score */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
      >
        <div className="flex items-center justify-center gap-1 mb-2">
          <span
            className="font-display font-bold text-5xl"
            style={{ color }}
          >
            {score}
          </span>
          <span className="text-2xl text-[var(--color-kids-text-muted)]">
            / {total}
          </span>
        </div>
        <p className="text-lg font-medium" style={{ color }}>
          {label}
        </p>
        <p className="text-sm text-[var(--color-kids-text-muted)] mt-1">
          {percentage}% correct
        </p>
      </motion.div>
    </div>
  )
}

// =============================================================================
// Progress Dots
// =============================================================================

interface ProgressDotsProps {
  total: number
  current: number
  answers: Record<number, number | null>
}

export function ProgressDots({ total, current, answers }: ProgressDotsProps) {
  return (
    <div className="flex items-center justify-center gap-2">
      {Array.from({ length: total }).map((_, index) => (
        <button
          key={index}
          className={cn(
            'w-3 h-3 rounded-full transition-all',
            index === current
              ? 'w-6 bg-[var(--color-kids-primary)]'
              : answers[index] !== undefined
                ? 'bg-[var(--color-kids-success)]'
                : 'bg-[var(--color-kids-surface-alt)]'
          )}
          aria-label={`Question ${index + 1}`}
        />
      ))}
    </div>
  )
}
